/**
 * "Who disconnected this call, and why" — display rules.
 *
 * The API returns what the backend RECORDED at the terminal transition
 * (`disconnectedBy` / `disconnectReason`). Calls that ended before that was
 * recorded return null, and those show as Unknown / Not recorded. Nothing here
 * derives a reason from the status or the duration.
 */
export type DisconnectedBy = 'user' | 'listener' | 'system' | 'admin';

export type DisconnectReason =
  | 'hung_up'
  | 'cancelled'
  | 'declined'
  | 'no_answer'
  | 'connect_timeout'
  | 'setup_failed'
  | 'network_lost'
  | 'insufficient_balance'
  | 'safety_flag'
  | 'admin_force_end'
  | 'time_limit';

const BY_LABEL: Record<DisconnectedBy, string> = {
  user: 'User',
  listener: 'Listener',
  system: 'System (automatic)',
  admin: 'Admin',
};

export const UNKNOWN_BY = 'Unknown';
export const NOT_RECORDED = 'Not recorded';

/** Typed loosely on purpose: the value comes off the wire and may be absent or new. */
export function disconnectedByLabel(by: string | null | undefined): string {
  if (!by) return UNKNOWN_BY;
  return BY_LABEL[by as DisconnectedBy] ?? `Unknown (${by})`;
}

export function disconnectReasonLabel(
  reason: string | null | undefined,
  by: string | null | undefined,
  /** The terminal lifecycle trigger, when known (API `outcomeTrigger`). */
  trigger?: string | null,
): string {
  if (!reason) return NOT_RECORDED;
  // Keyed on the trigger only: `system` + `cancelled` is also a ring closed
  // because its request already ended (`call_request_closed`).
  if (trigger === 'caller_app_gone') {
    return "Stopped automatically — the caller's app was no longer following it";
  }
  const who = by === 'listener' ? 'Listener' : by === 'user' ? 'User' : 'Participant';
  const whose = by === 'listener' ? "the listener's" : by === 'user' ? "the user's" : "a participant's";
  switch (reason as DisconnectReason) {
    case 'hung_up':
      return `${who} ended the call`;
    case 'cancelled':
      return `${who} ended the call before it connected`;
    case 'declined':
      return 'Listener declined the call';
    case 'no_answer':
      return 'No answer — the ring timed out';
    case 'connect_timeout':
      return 'Accepted, but both sides never joined in time';
    case 'setup_failed':
      return `Call setup failed on ${whose} device`;
    case 'network_lost':
      return 'Connection lost — heartbeats stopped';
    case 'insufficient_balance':
      return 'Insufficient coin balance';
    case 'safety_flag':
      return 'Ended by the safety system (critical flag)';
    case 'admin_force_end':
      return 'Force-ended by an admin';
    case 'time_limit':
      return 'Time limit reached (verification call length / night limit)';
    default:
      // A reason this panel build does not know yet — show it, don't hide it.
      return `Other (${reason})`;
  }
}

export function disconnectedByColor(by: string | null | undefined): string {
  switch (by) {
    case 'user':
      return 'blue';
    case 'listener':
      return 'purple';
    case 'system':
      return 'orange';
    case 'admin':
      return 'red';
    default:
      return 'default';
  }
}

/** A timestamp for display, never "1/1/1970" or "Invalid Date". */
export function formatWhen(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

export function formatDuration(s: number | null | undefined): string {
  if (s == null || !Number.isFinite(s)) return '—';
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}
